import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import Dashboard, { prefetchPortfolioData, WorkerInfo, getVMStatus } from './pages/Dashboard';
import Admin from './pages/Admin';
import Research from './pages/Research';
import Login from './pages/Login';
import TradeHistory from './pages/TradeHistory';

interface KillStatusLite {
  mode: string;
  system_paused?: boolean;
  connections_blocked?: boolean;
}

// ─── BotStatusPill: merges kill-switch mode + VM fleet health into one pill ────

function BotStatusPill({ token, killStatus }: { token: string; killStatus: KillStatusLite | null }) {
  const navigate = useNavigate();
  const [master, setMaster] = useState<WorkerInfo | null>(null);
  const [workers, setWorkers] = useState<WorkerInfo[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch('/api/workers/health', { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const d = await res.json();
          setMaster(d.master);
          setWorkers(d.workers || []);
        }
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [token]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!killStatus) return null;

  const vmOK = getVMStatus(master, workers) === 'running';
  const mode = killStatus.mode;

  type PillState = 'live-ok' | 'live-degraded' | 'passive-ok' | 'passive-degraded' | 'paused' | 'killed';
  let state: PillState;
  let label: string;
  if (mode === 'disconnected') { state = 'killed'; label = 'Killed'; }
  else if (mode === 'paused') { state = 'paused'; label = 'Paused'; }
  else if (mode === 'passive') { state = vmOK ? 'passive-ok' : 'passive-degraded'; label = vmOK ? 'Passive · OK' : 'Passive · Degraded'; }
  else { state = vmOK ? 'live-ok' : 'live-degraded'; label = vmOK ? 'Live · OK' : 'Live · Degraded'; }

  const pillStyles: Record<PillState, string> = {
    'live-ok': 'bg-status-green-soft border-border-green text-status-green-muted',
    'live-degraded': 'bg-status-amber-soft border-border-amber text-status-amber-muted',
    'passive-ok': 'bg-accent-soft border-accent text-text-secondary',
    'passive-degraded': 'bg-status-amber-soft border-border-amber text-status-amber-muted',
    'paused': 'bg-surface-2 border-border-light text-text-secondary',
    'killed': 'bg-status-red-soft border-border-red text-status-red-muted',
  };
  const dotStyles: Record<PillState, string> = {
    'live-ok': 'bg-status-green',
    'live-degraded': 'bg-status-amber',
    'passive-ok': 'bg-accent-hover',
    'passive-degraded': 'bg-status-amber',
    'paused': 'bg-text-muted',
    'killed': 'bg-status-red',
  };

  const allVMs = master ? [master, ...workers] : workers;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-pill border transition-colors hover:opacity-90 ${pillStyles[state]}`}
        title="Bot status — click for details"
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dotStyles[state]}`} />
        {label}
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-80 bg-surface border border-border-base rounded-card shadow-card z-50 p-4 space-y-3">
          <div>
            <p className="text-xs text-text-muted uppercase tracking-wide mb-1">Kill Chain State</p>
            <p className="text-sm text-text-primary">Mode: <span className="font-semibold">{mode}</span></p>
            <p className="text-xs text-text-secondary mt-1">
              System Paused: {killStatus.system_paused ? 'Yes' : 'No'} · Connections Blocked: {killStatus.connections_blocked ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-muted uppercase tracking-wide mb-1">VM Fleet</p>
            <div className="space-y-1">
              {allVMs.map(vm => (
                <div key={vm.id} className="flex items-center justify-between text-xs">
                  <span className="text-text-primary">{vm.name}</span>
                  <span className={vm.status === 'online' ? 'text-status-green' : vm.status === 'error' ? 'text-status-amber' : 'text-status-red'}>
                    {vm.status.toUpperCase()}
                  </span>
                </div>
              ))}
              {allVMs.length === 0 && <p className="text-xs text-text-muted">No VM data</p>}
            </div>
          </div>
          <button
            onClick={() => {
              setOpen(false);
              navigate('/admin?tab=system&view=details');
            }}
            className="w-full text-xs bg-status-blue-btn hover:bg-status-blue-btn-hover text-text-bright py-2 rounded-btn font-medium"
          >
            Go to Kill Switch →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── NavBarActions: uses useNavigate so must be inside Router context ──────────

interface HeaderSearchResult {
  ticker: string;
  exchange: string;
  company_name: string;
}

function HeaderSearch({ token }: { token: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<HeaderSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (query.trim().length < 1) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query, exchange: 'ALL' });
        const res = await fetch(`/api/explorer/search?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setResults(d.results || []); }
      } catch {} finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, token]);

  const selectResult = (r: HeaderSearchResult) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    navigate(`/explorer?ticker=${r.ticker}&exchange=${r.exchange}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-8 h-8 rounded-btn border border-border-base bg-surface inline-flex items-center justify-center text-text-secondary hover:border-accent hover:text-text-primary transition-colors"
        title="Search"
      >
        <span className="text-lg">🔍</span>
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-96 bg-surface border border-border-base rounded-card shadow-card z-50">
          <div className="p-3 border-b border-border-base">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search ticker or company name..."
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent"
            />
          </div>
          {searching ? (
            <div className="px-4 py-6 text-center text-text-muted text-sm">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-6 text-center text-text-muted text-sm">
              {query.trim() ? 'No matches' : 'Type to search Research → Search'}
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {results.map(r => (
                <button
                  key={`${r.exchange}-${r.ticker}`}
                  onClick={() => selectResult(r)}
                  className="w-full text-left px-4 py-3 hover:bg-surface-hover transition-colors border-b border-border-base last:border-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="font-bold text-sm font-mono text-text-bright">{r.ticker}</span>
                      <span className="text-text-secondary ml-3 text-sm truncate">{r.company_name}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${r.exchange === 'ASX' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'}`}>{r.exchange}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NavBarActions({ onLogout, userRole, token }: { onLogout: () => void; userRole: string; token: string }) {
  const navigate = useNavigate();
  const [killStatus, setKillStatus] = useState<KillStatusLite | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [bellAlerts, setBellAlerts] = useState<any[]>([]);
  const bellRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (userRole !== 'admin') return;
    const poll = async () => {
      try {
        const res = await fetch('/api/admin/kill-switch/status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d = await res.json();
          setKillStatus({ mode: d.mode || 'active', system_paused: d.system_paused, connections_blocked: d.connections_blocked });
        }
      } catch {}
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [userRole, token]);

  useEffect(() => {
    if (!token) return;
    const fetchUnread = () => {
      fetch('/api/admin/alerts/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setUnreadCount(d.count); })
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleBellOpen = async () => {
    if (bellOpen) { setBellOpen(false); return; }
    try {
      const r = await fetch('/api/admin/alerts?is_read=false&limit=10', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        setBellAlerts(d.items || []);
      }
      await fetch('/api/admin/alerts/read-all', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
    } catch {}
    setBellOpen(true);
  };

  const severityColour = (sev: string) => {
    if (sev === 'CRITICAL') return 'text-status-red';
    if (sev === 'HIGH') return 'text-status-amber';
    if (sev === 'MEDIUM') return 'text-status-amber';
    return 'text-text-secondary';
  };

  return (
    <>
      {userRole === 'guest' && (
        <span className="text-xs bg-accent-soft text-text-secondary px-2 py-1 rounded border border-accent">
          Guest Mode
        </span>
      )}
      <HeaderSearch token={token} />
      <div className="relative" ref={bellRef}>
        <button
          onClick={handleBellOpen}
          className="relative w-8 h-8 rounded-btn border border-border-base bg-surface inline-flex items-center justify-center text-text-secondary hover:border-accent hover:text-text-primary transition-colors"
          title="Notifications"
        >
          <span className="text-xl">🔔</span>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-accent text-text-bright text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-bg-deep">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
        {bellOpen && (
          <div className="absolute right-0 top-10 w-96 bg-surface border border-border-base rounded-card shadow-card z-50">
            <div className="px-4 py-3 border-b border-border-base flex items-center justify-between">
              <span className="font-semibold text-sm text-text-primary">Notifications</span>
              <button
                onClick={() => { setBellOpen(false); navigate('/admin?tab=Notifications'); }}
                className="text-xs text-status-blue hover:text-status-blue-muted"
              >
                View all →
              </button>
            </div>
            {bellAlerts.length === 0 ? (
              <div className="px-4 py-6 text-center text-text-muted text-sm">No unread notifications</div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {bellAlerts.map(a => (
                  <div
                    key={a.id}
                    onClick={() => { setBellOpen(false); navigate(`/admin?tab=Notifications&alert=${a.id}`); }}
                    className="px-4 py-3 border-b border-border-base/50 hover:bg-surface-hover cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className={`text-xs font-semibold uppercase ${severityColour(a.severity)}`}>{a.severity}</span>
                      <span className="text-xs text-text-muted shrink-0">
                        {new Date(a.created_at.endsWith('Z') ? a.created_at : a.created_at + 'Z').toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-text-primary mt-1 leading-snug">{a.short_body || a.message || a.heading}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {userRole === 'admin' && <BotStatusPill token={token} killStatus={killStatus} />}
      <button
        onClick={onLogout}
        className="bg-surface-2 hover:bg-surface-hover text-text-bright text-sm px-3 py-1 rounded"
      >
        Logout
      </button>
    </>
  );
}

function App() {
  const getValidToken = () => {
    const token = sessionStorage.getItem('token');
    const expiry = sessionStorage.getItem('token_expiry');
    if (!token || !expiry) return null;
    if (new Date().getTime() > parseInt(expiry)) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('token_expiry');
      return null;
    }
    return token;
  };

  const [token, setToken] = useState<string | null>(getValidToken());
  const [userRole, setUserRole] = useState<string>('admin');

  useEffect(() => {
    if (!token) return;
    // Pre-fetch dashboard data every 60s so navigating back is instant
    prefetchPortfolioData(token);
    const interval = setInterval(() => prefetchPortfolioData(token), 60000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (!token) { setUserRole('admin'); return; }
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setUserRole(d.role || 'admin'); sessionStorage.setItem('user_role', d.role || 'admin'); })
      .catch(() => {});
  }, [token]);

  const handleLogin = (newToken: string) => {
    const expiry = new Date().getTime() + (8 * 60 * 60 * 1000);
    sessionStorage.setItem('token', newToken);
    sessionStorage.setItem('token_expiry', expiry.toString());
    setToken(newToken);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('token_expiry');
    setToken(null);
  };

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  // Sub-page flyout: hover shows a preview/shortcut list; the top-level NavLink's own
  // click behavior is untouched, so a direct click still navigates exactly as before.
  const navLink = (to: string, label: string, subPages?: { to: string; label: string }[]) => (
    <div className="relative group">
      <NavLink
        to={to}
        end={to === '/'}
        className={({ isActive }) =>
          `px-3 py-2 rounded text-sm font-medium transition-colors ${
            isActive ? 'bg-status-green-btn text-text-bright' : 'text-text-primary hover:bg-surface-hover'
          }`
        }
      >
        {label}
      </NavLink>
      {subPages && subPages.length > 0 && (
        <div className="absolute left-0 top-full pt-1 hidden group-hover:block z-50">
          <div className="bg-surface border border-border-base rounded-card shadow-lg py-1 min-w-[190px]">
            {subPages.map(sp => (
              <NavLink
                key={sp.label}
                to={sp.to}
                className="block px-3 py-2 text-sm text-text-primary hover:bg-surface-hover whitespace-nowrap"
              >
                {sp.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Router>
      <div className="min-h-screen bg-bg-deep text-text-bright">
        {/* Navigation Bar */}
        <nav className="bg-surface border-b border-border-base sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <div className="flex items-center space-x-3">
                <span className="text-2xl">📈</span>
                <span className="font-extrabold text-sm uppercase tracking-wide text-accent-hover">TradingBot</span>
              </div>

              {/* Nav Links */}
              <div className="flex space-x-1">
                {navLink('/', 'Dashboard')}
                {navLink('/research', 'Research', [
                  { to: '/research', label: 'Watch This' },
                  { to: '/research', label: 'Watch List' },
                  { to: '/research', label: 'Scanner Flags' },
                  { to: '/research', label: 'Search' },
                ])}
                {navLink('/trade-history', 'Trade History')}
                {userRole === 'admin' && navLink('/admin', 'Admin', [
                  { to: '/admin', label: 'Data Management' },
                  { to: '/admin?tab=exchanges', label: 'Exchange Management' },
                  { to: '/admin?tab=monitoring', label: 'System Monitoring' },
                  { to: '/admin?tab=Notifications', label: 'Notifications' },
                  { to: '/admin?tab=system', label: 'System' },
                ])}
              </div>

              {/* Emergency Stop + Logout (inside Router so useNavigate works) */}
              <div className="flex items-center space-x-3">
                <NavBarActions onLogout={handleLogout} userRole={userRole} token={token || ''} />
              </div>
            </div>
          </div>
        </nav>

        {/* Page Content */}
        <main className="max-w-7xl mx-auto px-4 py-6">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/research" element={<Research />} />
            <Route path="/trade-history" element={<TradeHistory />} />
            <Route path="/admin" element={<Admin />} />
            {/* Legacy routes redirect to research */}
            <Route path="/watch-this" element={<Research />} />
            <Route path="/watchlist" element={<Research />} />
            <Route path="/explorer" element={<Research />} />
            <Route path="/data-sources" element={<Admin />} />
            <Route path="/signals" element={<Admin />} />
            <Route path="/strategy" element={<Admin />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
